import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Pagination,
  Row,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from 'react-bootstrap';
import Select from 'react-select';
import { FaEdit, FaInfoCircle, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import * as api from '~/Services/Service';
import {
  buscarOpcaoPorValor,
  formatarDataHora,
  montarOpcoesDepartamentos,
  montarOpcoesUsuarios,
  opcoesAcessoDepartamento,
} from './helpers';

const assuntoInicial = {
  codigo: null,
  tipo: 'J',
  assunto: '',
  departamento: null,
  descricao: '',
  trava_rescisao: 'N',
};

const usuarioDepartamentoInicial = {
  codigo_config: null,
  codigo_usuario: null,
  departamento: null,
  tipo_usuario: 'colaborador',
};

const parametrosInicial = {
  codigo: null,
  prioridade_alta: '',
  prioridade_media: '',
  prioridade_baixa: '',
  prazo_reabertura: '',
};

const TAMANHO_PAGINA_ASSUNTOS = 8;

const modalConfiguracoesStyles = {
  modalBody: {
    backgroundColor: '#ffffff',
    padding: '12px 18px 18px',
  },
  tabsWrapper: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    padding: 0,
    boxShadow: 'none',
  },
  sectionCard: {
    border: '1px solid #dde5ef',
    borderRadius: '12px',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
    marginBottom: '14px',
  },
  sectionHeader: {
    background: 'linear-gradient(90deg, #f8fafc 0%, #eef4fb 100%)',
    borderBottom: '1px solid #dde5ef',
    padding: '10px 14px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 700,
    color: '#24324a',
  },
  sectionBody: {
    padding: '14px',
  },
  timelineItem: {
    position: 'relative',
    paddingLeft: '22px',
    marginBottom: '12px',
  },
  timelineMarker: {
    position: 'absolute',
    left: 0,
    top: '4px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#0d6efd',
    boxShadow: '0 0 0 4px rgba(13, 110, 253, 0.12)',
  },
  timelineContent: {
    border: '1px solid #e3e8f0',
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    padding: '10px 12px',
  },
  emptyState: {
    border: '1px dashed #cbd5e1',
    borderRadius: '10px',
    padding: '18px',
    textAlign: 'center',
    color: '#64748b',
    backgroundColor: '#f8fafc',
  },
  campoTexto: {
    borderRadius: '6px',
    width: '100%',
    minHeight: '38px',
    height: '38px',
    fontFamily: "'Open Sans', Arial, sans-serif",
    boxSizing: 'border-box',
    border: '1px solid #ced4da',
    backgroundColor: '#fff',
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    lineHeight: '1.5',
  },
  campoTextarea: {
    borderRadius: '6px',
    width: '100%',
    minHeight: '98px',
    fontFamily: "'Open Sans', Arial, sans-serif",
    boxSizing: 'border-box',
    border: '1px solid #ced4da',
    backgroundColor: '#fff',
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    lineHeight: '1.5',
  },
};

const SecaoCardConfiguracao = ({ titulo, children, actions }) => (
  <Card style={modalConfiguracoesStyles.sectionCard}>
    <Card.Header style={modalConfiguracoesStyles.sectionHeader}>
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <h6 style={modalConfiguracoesStyles.sectionTitle}>{titulo}</h6>
        {actions && <div>{actions}</div>}
      </div>
    </Card.Header>
    <Card.Body style={modalConfiguracoesStyles.sectionBody}>
      {children}
    </Card.Body>
  </Card>
);

const ModalConfiguracoes = ({
  show,
  lookups,
  onHide,
  onAtualizado,
}) => {
  const [abaAtiva, setAbaAtiva] = useState('assunto');
  const [assuntos, setAssuntos] = useState([]);
  const [paginaAssuntos, setPaginaAssuntos] = useState(1);
  const [departamentosConfig, setDepartamentosConfig] = useState({
    departamentos: [],
    gestores: [],
    colaboradores: [],
  });
  const [parametros, setParametros] = useState(parametrosInicial);
  const [historico, setHistorico] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [assuntoForm, setAssuntoForm] = useState(null);
  const [usuarioForm, setUsuarioForm] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);

  const departamentosOpcoes =
    departamentosConfig && departamentosConfig.departamentos
      ? departamentosConfig.departamentos
      : lookups && lookups.departamentos
        ? lookups.departamentos
        : [];

  const opcoesDepartamentos = montarOpcoesDepartamentos(
    departamentosOpcoes
  );
  const opcoesUsuarios = montarOpcoesUsuarios(usuarios);

  const totalPaginasAssuntos = useMemo(() => {
    return Math.max(
      1,
      Math.ceil((assuntos.length || 0) / TAMANHO_PAGINA_ASSUNTOS)
    );
  }, [assuntos.length]);

  const assuntosPaginados = useMemo(() => {
    const inicio = (paginaAssuntos - 1) * TAMANHO_PAGINA_ASSUNTOS;
    return assuntos.slice(inicio, inicio + TAMANHO_PAGINA_ASSUNTOS);
  }, [assuntos, paginaAssuntos]);

  const carregarAssuntos = async () => {
    const resposta = await api.listarConfiguracaoAssuntosJuridicos({});
    setAssuntos(resposta || []);
  };

  const carregarDepartamentos = async () => {
    const resposta = await api.listarDepartamentosConfigJuridica({});
    setDepartamentosConfig({
      departamentos:
        resposta && resposta.departamentos ? resposta.departamentos : [],
      gestores: resposta && resposta.gestores ? resposta.gestores : [],
      colaboradores:
        resposta && resposta.colaboradores ? resposta.colaboradores : [],
    });
  };

  const carregarUsuarios = async () => {
    const resposta = await api.obterUsuarios({
      status: 'ativos',
      validade: 'todos',
    });
    setUsuarios(resposta || []);
  };

  const carregarParametros = async () => {
    const resposta = await api.buscarParametrosSolicitacaoJuridica();
    const dados = resposta || {};
    setParametros({
      codigo: dados.codigo || null,
      prioridade_alta:
        dados.prioridade_alta !== null && dados.prioridade_alta !== undefined
          ? dados.prioridade_alta
          : '',
      prioridade_media:
        dados.prioridade_media !== null && dados.prioridade_media !== undefined
          ? dados.prioridade_media
          : '',
      prioridade_baixa:
        dados.prioridade_baixa !== null && dados.prioridade_baixa !== undefined
          ? dados.prioridade_baixa
          : '',
      prazo_reabertura:
        dados.prazo_reabertura !== null && dados.prazo_reabertura !== undefined
          ? dados.prazo_reabertura
          : '',
    });
  };

  const carregarHistorico = async () => {
    const resposta = await api.buscarHistoricoConfigSolicitacaoJuridica();
    setHistorico(resposta || []);
  };

  const carregarTudo = async () => {
    setCarregando(true);
    setErro('');
    try {
      await Promise.all([
        carregarAssuntos(),
        carregarDepartamentos(),
        carregarUsuarios(),
        carregarParametros(),
        carregarHistorico(),
      ]);
    } catch (err) {
      setErro(extrairMensagemErro(err) || 'Erro ao carregar configurações.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (show) {
      setAbaAtiva('assunto');
      setPaginaAssuntos(1);
      setAssuntoForm(null);
      setUsuarioForm(null);
      setConfirmacao(null);
      carregarTudo();
    }
  }, [show]);

  useEffect(() => {
    if (paginaAssuntos > totalPaginasAssuntos) {
      setPaginaAssuntos(totalPaginasAssuntos);
    }
  }, [paginaAssuntos, totalPaginasAssuntos]);

  const gruposDepartamento = useMemo(() => {
    const itens = [];
    const gestores =
      departamentosConfig && departamentosConfig.gestores
        ? departamentosConfig.gestores
        : [];
    const colaboradores =
      departamentosConfig && departamentosConfig.colaboradores
        ? departamentosConfig.colaboradores
        : [];

    gestores.forEach(item => itens.push({ ...item, tipo_usuario: 'gestor' }));
    colaboradores.forEach(item =>
      itens.push({ ...item, tipo_usuario: 'colaborador' })
    );

    const grupos = {};
    itens.forEach(item => {
      const chave = item.departamento || 'sem-departamento';
      if (!grupos[chave]) {
        grupos[chave] = {
          departamento: item.departamento,
          departamento_descricao: item.departamento_descricao,
          usuarios: [],
        };
      }
      grupos[chave].usuarios.push(item);
    });

    return Object.keys(grupos).map(chave => grupos[chave]);
  }, [departamentosConfig]);

  const confirmarAcao = (title, message, onConfirm) => {
    setConfirmacao({ title, message, onConfirm });
  };

  const fecharConfirmacao = confirmado => {
    const atual = confirmacao;
    setConfirmacao(null);
    if (confirmado && atual && atual.onConfirm) {
      atual.onConfirm();
    }
  };

  const salvarAssunto = async () => {
    if (!assuntoForm.assunto || !assuntoForm.departamento) {
      toast.error('Informe assunto e departamento.');
      return;
    }

    setSalvando(true);
    try {
      await api.salvarConfiguracaoAssuntoJuridico({
        codigo: assuntoForm.codigo,
        tipo: assuntoForm.tipo || 'J',
        assunto: assuntoForm.assunto,
        departamento: assuntoForm.departamento,
        descricao: assuntoForm.descricao,
        trava_rescisao: assuntoForm.trava_rescisao || 'N',
      });
      toast.success('Assunto salvo com sucesso.');
      setAssuntoForm(null);
      await carregarAssuntos();
      if (onAtualizado) onAtualizado();
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Erro ao salvar assunto.');
    } finally {
      setSalvando(false);
    }
  };

  const removerAssunto = item => {
    confirmarAcao(
      'Remover assunto',
      `Confirma remover o assunto ${item.assunto}?`,
      async () => {
        setSalvando(true);
        try {
          await api.removerConfiguracaoAssuntoJuridico({ codigo: item.codigo });
          toast.success('Assunto removido com sucesso.');
          await carregarAssuntos();
          if (onAtualizado) onAtualizado();
        } catch (err) {
          toast.error(extrairMensagemErro(err) || 'Erro ao remover assunto.');
        } finally {
          setSalvando(false);
        }
      }
    );
  };

  const salvarUsuarioDepartamento = async () => {
    if (
      !usuarioForm.departamento ||
      !usuarioForm.codigo_usuario ||
      !usuarioForm.tipo_usuario
    ) {
      toast.error('Informe departamento, usuario e acesso.');
      return;
    }

    setSalvando(true);
    try {
      await api.salvarUsuarioDepartamentoJuridica({
        codigo_config: usuarioForm.codigo_config || parametros.codigo,
        codigo_usuario: usuarioForm.codigo_usuario,
        departamento: usuarioForm.departamento,
        tipo_usuario: usuarioForm.tipo_usuario,
      });
      toast.success('Usuário vinculado com sucesso.');
      setUsuarioForm(null);
      await carregarDepartamentos();
      if (onAtualizado) onAtualizado();
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Erro ao salvar usuário.');
    } finally {
      setSalvando(false);
    }
  };

  const removerUsuarioDepartamento = item => {
    confirmarAcao(
      'Remover usuário',
      `Confirma remover ${item.nome_usuario} do departamento ${item.departamento_descricao}?`,
      async () => {
        setSalvando(true);
        try {
          await api.removerUsuarioDepartamentoJuridica({
            codigo_config: item.codigo_config,
            codigo_usuario: item.codigo_usuario,
            departamento: item.departamento,
            tipo_usuario: item.tipo_usuario,
          });
          toast.success('Usuario removido com sucesso.');
          await carregarDepartamentos();
          if (onAtualizado) onAtualizado();
        } catch (err) {
          toast.error(extrairMensagemErro(err) || 'Erro ao remover usuario.');
        } finally {
          setSalvando(false);
        }
      }
    );
  };

  const salvarParametros = async () => {
    setSalvando(true);
    try {
      await api.salvarParametrosSolicitacaoJuridica({
        codigo: parametros.codigo,
        prioridade_alta: parametros.prioridade_alta,
        prioridade_media: parametros.prioridade_media,
        prioridade_baixa: parametros.prioridade_baixa,
        prazo_reabertura: parametros.prazo_reabertura,
      });
      toast.success('Parâmetros salvos com sucesso.');
      await carregarParametros();
      await carregarHistorico();
      if (onAtualizado) onAtualizado();
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Erro ao salvar parâmetros.');
    } finally {
      setSalvando(false);
    }
  };

  const renderPaginacaoAssuntos = () => {
    if (totalPaginasAssuntos <= 1) return null;

    const itensPaginacao = [];
    const maxBotoes = 5;
    let inicio = Math.max(1, paginaAssuntos - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginasAssuntos, inicio + maxBotoes - 1);

    if (fim - inicio < maxBotoes - 1) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }

    const mudarPaginaAssuntos = numero => {
      if (
        numero < 1 ||
        numero > totalPaginasAssuntos ||
        numero === paginaAssuntos
      ) {
        return;
      }

      setPaginaAssuntos(numero);
    };

    itensPaginacao.push(
      <Pagination.First
        key="first"
        disabled={paginaAssuntos === 1}
        onClick={() => mudarPaginaAssuntos(1)}
      />
    );
    itensPaginacao.push(
      <Pagination.Prev
        key="prev"
        disabled={paginaAssuntos === 1}
        onClick={() => mudarPaginaAssuntos(paginaAssuntos - 1)}
      />
    );

    if (inicio > 1) {
      itensPaginacao.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let numero = inicio; numero <= fim; numero += 1) {
      itensPaginacao.push(
        <Pagination.Item
          key={`page-${numero}`}
          active={numero === paginaAssuntos}
          onClick={() => mudarPaginaAssuntos(numero)}
        >
          {numero}
        </Pagination.Item>
      );
    }

    if (fim < totalPaginasAssuntos) {
      itensPaginacao.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itensPaginacao.push(
      <Pagination.Next
        key="next"
        disabled={paginaAssuntos === totalPaginasAssuntos}
        onClick={() => mudarPaginaAssuntos(paginaAssuntos + 1)}
      />
    );
    itensPaginacao.push(
      <Pagination.Last
        key="last"
        disabled={paginaAssuntos === totalPaginasAssuntos}
        onClick={() => mudarPaginaAssuntos(totalPaginasAssuntos)}
      />
    );

    const inicioRegistro =
      assuntos.length === 0 ? 0 : (paginaAssuntos - 1) * TAMANHO_PAGINA_ASSUNTOS + 1;
    const fimRegistro = Math.min(
      paginaAssuntos * TAMANHO_PAGINA_ASSUNTOS,
      assuntos.length || 0
    );

    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap px-3 py-2">
        <span className="small text-muted mb-2 mb-md-0">
          Mostrando {inicioRegistro} até {fimRegistro} de {assuntos.length || 0} registros
        </span>
        <Pagination size="sm" className="mb-0">
          {itensPaginacao}
        </Pagination>
      </div>
    );
  };

  const renderDescricaoAssunto = item => {
    if (!item.descricao) {
      return <span className="small text-muted ml-2">Sem descrição</span>;
    }

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-assunto-${item.codigo}`}>
            {item.descricao}
          </Tooltip>
        }
      >
        <span
          className="small text-primary ml-2"
          style={{ cursor: 'help', whiteSpace: 'nowrap' }}
        >
          <FaInfoCircle />
        </span>
      </OverlayTrigger>
    );
  };

  const renderAssuntos = () => {
    if (assuntoForm) {
      return (
        <SecaoCardConfiguracao
          titulo={assuntoForm.codigo ? 'Editar assunto' : 'Novo assunto'}
        >
          <Form.Group>
            <Form.Label className="small mb-1">Assunto</Form.Label>
            <Form.Control
              size="sm"
              value={assuntoForm.assunto}
              style={modalConfiguracoesStyles.campoTexto}
              onChange={event =>
                setAssuntoForm(prev => ({ ...prev, assunto: event.target.value }))
              }
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Departamento</Form.Label>
            <Select
              isClearable
              options={opcoesDepartamentos}
              value={buscarOpcaoPorValor(
                opcoesDepartamentos,
                assuntoForm.departamento
              )}
              onChange={opcao =>
                setAssuntoForm(prev => ({
                  ...prev,
                  departamento: opcao ? opcao.value : null,
                }))
              }
              components={{ DropdownIndicator }}
              styles={customStyles}
              noOptionsMessage={() => 'Nenhum departamento encontrado'}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={assuntoForm.descricao}
              style={modalConfiguracoesStyles.campoTextarea}
              onChange={event =>
                setAssuntoForm(prev => ({
                  ...prev,
                  descricao: event.target.value,
                }))
              }
            />
          </Form.Group>
          <div className="text-right">
            <Button
              size="sm"
              variant="outline-secondary"
              className="mr-2"
              disabled={salvando}
              onClick={() => setAssuntoForm(null)}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={salvando}
              onClick={salvarAssunto}
            >
              Salvar
            </Button>
          </div>
        </SecaoCardConfiguracao>
      );
    }

    return (
      <SecaoCardConfiguracao titulo="Assuntos cadastrados">
        <Table
          bordered
          hover
          size="sm"
          className="mb-2"
          style={{ tableLayout: 'fixed' }}
        >
          <thead>
            <tr>
              <th style={{ width: 76, padding: '8px 10px' }}>Ações</th>
              <th style={{ padding: '8px 10px' }}>Assunto</th>
              <th style={{ width: 170, padding: '8px 10px' }}>
                Departamento Responsável
              </th>
            </tr>
          </thead>
          <tbody>
            {assuntos.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  Nenhum assunto encontrado.
                </td>
              </tr>
            )}
            {assuntosPaginados.map(item => (
              <tr key={item.codigo}>
                <td
                  className="text-center text-nowrap"
                  style={{ padding: '6px 8px', verticalAlign: 'top' }}
                >
                  <Button
                    size="sm"
                    variant="link"
                    title="Editar"
                    style={{ padding: '0 4px' }}
                    onClick={() =>
                      setAssuntoForm({
                        codigo: item.codigo,
                        tipo: item.tipo || 'J',
                        assunto: item.assunto || '',
                        departamento: item.departamento || null,
                        descricao: item.descricao || '',
                        trava_rescisao: item.trava_rescisao || 'N',
                      })
                    }
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className="text-danger"
                    title="Excluir"
                    style={{ padding: '0 4px' }}
                    onClick={() => removerAssunto(item)}
                  >
                    <FaTrash />
                  </Button>
                </td>
                <td
                  style={{
                    padding: '7px 10px',
                    lineHeight: '1.25',
                    verticalAlign: 'top',
                  }}
                >
                  <div className="d-flex align-items-center flex-wrap">
                    <span style={{ fontWeight: 600 }}>{item.assunto}</span>
                    {renderDescricaoAssunto(item)}
                  </div>
                </td>
                <td
                  style={{
                    padding: '7px 10px',
                    lineHeight: '1.25',
                    verticalAlign: 'top',
                  }}
                >
                  {item.departamento_descricao}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {renderPaginacaoAssuntos()}
        <div className="text-right mt-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => setAssuntoForm(assuntoInicial)}
          >
            <FaPlus className="mr-1" /> Novo
          </Button>
        </div>
      </SecaoCardConfiguracao>
    );
  };

  const renderDepartamentos = () => {
    if (usuarioForm) {
      return (
        <SecaoCardConfiguracao
          titulo={usuarioForm.codigo_config ? 'Editar usuário' : 'Novo usuário'}
        >
          <Form.Group>
            <Form.Label className="small mb-1">Departamento</Form.Label>
            <Select
              isClearable
              options={opcoesDepartamentos}
              value={buscarOpcaoPorValor(
                opcoesDepartamentos,
                usuarioForm.departamento
              )}
              onChange={opcao =>
                setUsuarioForm(prev => ({
                  ...prev,
                  departamento: opcao ? opcao.value : null,
                }))
              }
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
          </Form.Group>
          <Row>
            <Col md={8}>
              <Form.Group>
                <Form.Label className="small mb-1">Usuário</Form.Label>
                <Select
                  isClearable
                  options={opcoesUsuarios}
                  value={buscarOpcaoPorValor(
                    opcoesUsuarios,
                    usuarioForm.codigo_usuario
                  )}
                  onChange={opcao =>
                    setUsuarioForm(prev => ({
                      ...prev,
                      codigo_usuario: opcao ? opcao.value : null,
                    }))
                  }
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                  noOptionsMessage={() => 'Nenhum usuário encontrado'}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small mb-1">Acesso</Form.Label>
                <Select
                  options={opcoesAcessoDepartamento}
                  value={buscarOpcaoPorValor(
                    opcoesAcessoDepartamento,
                    usuarioForm.tipo_usuario
                  )}
                  onChange={opcao =>
                    setUsuarioForm(prev => ({
                      ...prev,
                      tipo_usuario: opcao ? opcao.value : 'colaborador',
                    }))
                  }
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="text-right">
            <Button
              size="sm"
              variant="outline-secondary"
              className="mr-2"
              disabled={salvando}
              onClick={() => setUsuarioForm(null)}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={salvando}
              onClick={salvarUsuarioDepartamento}
            >
              Salvar
            </Button>
          </div>
        </SecaoCardConfiguracao>
      );
    }

    return (
      <>
        <div
          style={{
            maxHeight: '58vh',
            overflowY: 'auto',
            paddingRight: '8px',
          }}
        >
          {gruposDepartamento.length === 0 && (
            <p className="text-muted text-center my-4">
              Nenhum departamento configurado.
            </p>
          )}
          {gruposDepartamento.map(grupo => (
            <Card
              className="mb-3"
              key={grupo.departamento}
              style={modalConfiguracoesStyles.sectionCard}
            >
              <Card.Header
                className="py-2 d-flex justify-content-between align-items-center"
                style={modalConfiguracoesStyles.sectionHeader}
              >
                <strong>{grupo.departamento_descricao}</strong>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() =>
                    setUsuarioForm({
                      ...usuarioDepartamentoInicial,
                      departamento: grupo.departamento,
                    })
                  }
                >
                    <FaPlus className="mr-1" /> Adicionar Usuário
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: '58%', padding: '8px 10px' }}>
                        Usuário
                      </th>
                      <th style={{ width: '24%', padding: '8px 10px' }}>
                        Acesso
                      </th>
                      <th
                        className="text-center"
                        style={{ width: '18%', padding: '8px 10px' }}
                      >
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.usuarios.map(item => (
                      <tr
                        key={`${item.departamento}-${item.codigo_usuario}-${item.tipo_usuario}`}
                      >
                        <td
                          style={{
                            padding: '7px 10px',
                            lineHeight: '1.25',
                            verticalAlign: 'middle',
                          }}
                        >
                          {item.nome_usuario}
                        </td>
                        <td
                          style={{
                            padding: '7px 10px',
                            lineHeight: '1.25',
                            verticalAlign: 'middle',
                          }}
                        >
                          {item.tipo_usuario === 'gestor'
                            ? 'Gestor'
                            : 'Colaborador'}
                        </td>
                        <td
                          className="text-center text-nowrap"
                          style={{
                            padding: '6px 8px',
                            lineHeight: '1.25',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Button
                            size="sm"
                            variant="link"
                            title="Editar"
                            style={{ padding: '0 4px' }}
                            onClick={() =>
                              setUsuarioForm({
                                codigo_config: item.codigo_config,
                                codigo_usuario: item.codigo_usuario,
                                departamento: item.departamento,
                                tipo_usuario: item.tipo_usuario,
                              })
                            }
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            className="text-danger"
                            title="Excluir"
                            style={{ padding: '0 4px' }}
                            onClick={() => removerUsuarioDepartamento(item)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ))}
        </div>
        <div className="text-right mt-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => setUsuarioForm(usuarioDepartamentoInicial)}
          >
            <FaPlus className="mr-1" /> Novo Departamento
          </Button>
        </div>
      </>
    );
  };

  const renderParametros = () => (
    <div style={{ maxWidth: 520 }}>
      <Card className="mb-3">
        <Card.Body>
          <h6>Envio de e-mail com resumo por prioridade</h6>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">Alta</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={parametros.prioridade_alta}
                  style={modalConfiguracoesStyles.campoTexto}
                  onChange={event =>
                    setParametros(prev => ({
                      ...prev,
                      prioridade_alta: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">Média</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={parametros.prioridade_media}
                  style={modalConfiguracoesStyles.campoTexto}
                  onChange={event =>
                    setParametros(prev => ({
                      ...prev,
                      prioridade_media: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">Baixa</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={parametros.prioridade_baixa}
                  style={modalConfiguracoesStyles.campoTexto}
                  onChange={event =>
                    setParametros(prev => ({
                      ...prev,
                      prioridade_baixa: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Form.Group>
            <Form.Label className="small mb-1">
              Prazo para reabertura da solicitação
            </Form.Label>
            <Form.Control
              size="sm"
              type="number"
              value={parametros.prazo_reabertura}
              style={modalConfiguracoesStyles.campoTexto}
              onChange={event =>
                setParametros(prev => ({
                  ...prev,
                  prazo_reabertura: event.target.value,
                }))
              }
            />
          </Form.Group>
        </Card.Body>
      </Card>
      <div className="text-right mt-3">
        <Button
          size="sm"
          variant="primary"
          disabled={salvando}
          onClick={salvarParametros}
        >
          Salvar parâmetros
        </Button>
      </div>
    </div>
  );

  const renderHistorico = () => (
    <SecaoCardConfiguracao titulo="Histórico de configurações">
      {historico.length === 0 && (
        <div style={modalConfiguracoesStyles.emptyState}>
          Nenhum histórico encontrado.
        </div>
      )}
      {historico.map((item, indice) => (
        <div
          key={`${item.codigo}-${item.data}-${indice}`}
          style={modalConfiguracoesStyles.timelineItem}
        >
          <span style={modalConfiguracoesStyles.timelineMarker} />
          <div style={modalConfiguracoesStyles.timelineContent}>
            <div className="d-flex justify-content-between flex-wrap">
              <strong>{item.titulo}</strong>
              <span className="small text-muted">
                {formatarDataHora(item.data)}
              </span>
            </div>
            <div className="small text-muted">{item.usuario_nome}</div>
            <div style={{ lineHeight: '1.45' }}>{item.texto}</div>
          </div>
        </div>
      ))}
    </SecaoCardConfiguracao>
  );

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Configurações</Modal.Title>
        </Modal.Header>
        <Modal.Body style={modalConfiguracoesStyles.modalBody}>
          {erro && <Alert variant="danger">{erro}</Alert>}
          {carregando && <Loading />}
          {!carregando && (
            <div style={modalConfiguracoesStyles.tabsWrapper}>
              <Tabs activeKey={abaAtiva} onSelect={setAbaAtiva}>
                <Tab eventKey="assunto" title="Assunto">
                  <div className="pt-3">{renderAssuntos()}</div>
                </Tab>
                <Tab eventKey="departamentos" title="Departamentos">
                  <div className="pt-3">{renderDepartamentos()}</div>
                </Tab>
                <Tab eventKey="parametros" title="Parâmetros">
                  <div className="pt-3">{renderParametros()}</div>
                </Tab>
                <Tab eventKey="historico" title="Histórico">
                  <div className="pt-3">{renderHistorico()}</div>
                </Tab>
              </Tabs>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ModalConfirmacao
        show={!!confirmacao}
        title={confirmacao ? confirmacao.title : ''}
        message={confirmacao ? confirmacao.message : ''}
        onClose={fecharConfirmacao}
      />
    </>
  );
};

export default ModalConfiguracoes;
